import React from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const PasswordStrengthIndicator = ({ password, showRequirements = true }) => {
  const requirements = [
    {
      test: (pwd) => pwd.length >= 8 && pwd.length <= 16,
      label: '8-16 characters',
      key: 'length'
    },
    {
      test: (pwd) => /[a-z]/.test(pwd),
      label: 'One lowercase letter (a-z)',
      key: 'lowercase'
    },
    {
      test: (pwd) => /[A-Z]/.test(pwd),
      label: 'One uppercase letter (A-Z)',
      key: 'uppercase'
    },
    {
      test: (pwd) => /[0-9]/.test(pwd),
      label: 'One number (0-9)',
      key: 'number'
    },
    {
      test: (pwd) => /[@#$!%&]/.test(pwd),
      label: 'One special character (@, #, $, !, %, &)',
      key: 'special'
    }
  ];

  const weakPasswords = [
    'password', 'password123', '12345678', 'qwerty123',
    'admin123', 'welcome123', 'letmein123', 'password1'
  ];

  const hasRepeatedChars = /(.)\1{3,}/.test(password);
  const isWeakPassword = weakPasswords.includes(password.toLowerCase());
  
  const passedRequirements = requirements.filter(req => req.test(password));
  const strengthPercentage = (passedRequirements.length / requirements.length) * 100;
  
  const getStrengthColor = () => {
    if (strengthPercentage >= 100 && !isWeakPassword && !hasRepeatedChars) return '#10b981'; // Green
    if (strengthPercentage >= 80) return '#f59e0b'; // Yellow
    if (strengthPercentage >= 40) return '#f97316'; // Orange
    return '#ef4444'; // Red
  };

  const getStrengthText = () => {
    if (strengthPercentage >= 100 && !isWeakPassword && !hasRepeatedChars) return 'Strong';
    if (strengthPercentage >= 80) return 'Good';
    if (strengthPercentage >= 40) return 'Fair';
    return 'Weak';
  };

  if (!password || !showRequirements) return null;

  return (
    <div style={{
      marginTop: '12px',
      padding: '16px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    }}>
      {/* Strength indicator */}
      <div style={{marginBottom: '12px'}}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '4px'
        }}>
          <span style={{fontSize: '14px', fontWeight: '500', color: '#374151'}}>
            Password Strength: 
          </span>
          <span style={{
            fontSize: '14px',
            fontWeight: '600',
            color: getStrengthColor()
          }}>
            {getStrengthText()}
          </span>
        </div>
        
        {/* Progress bar */}
        <div style={{
          width: '100%',
          height: '4px',
          backgroundColor: '#e5e7eb',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${strengthPercentage}%`,
            height: '100%',
            backgroundColor: getStrengthColor(),
            transition: 'all 0.3s ease'
          }} />
        </div>
      </div>

      {/* Requirements list */}
      <div style={{fontSize: '14px'}}>
        <div style={{marginBottom: '8px', fontWeight: '500', color: '#374151'}}>
          Requirements:
        </div>
        
        {requirements.map((req) => {
          const passed = req.test(password);
          return (
            <div key={req.key} style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '4px',
              color: passed ? '#10b981' : '#6b7280'
            }}>
              {passed ? (
                <CheckCircle size={16} style={{marginRight: '8px', color: '#10b981'}} />
              ) : (
                <XCircle size={16} style={{marginRight: '8px', color: '#e5e7eb'}} />
              )}
              <span>{req.label}</span>
            </div>
          );
        })}

        {/* Additional warnings */}
        {isWeakPassword && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '8px',
            color: '#ef4444'
          }}>
            <AlertCircle size={16} style={{marginRight: '8px'}} />
            <span>Avoid common passwords</span>
          </div>
        )}

        {hasRepeatedChars && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginTop: '4px',
            color: '#ef4444'
          }}>
            <AlertCircle size={16} style={{marginRight: '8px'}} />
            <span>Too many repeated characters</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;